import { useState, useCallback } from 'react';
import { useSandboxRunner } from './SandboxRunnerContext';
import type { TestRunResult, TestProgressState, SuiteProgressItem, TestProgressItem, TestItemState } from './types';

// ─── State → visual mapping ─────────────────────────────────────────────────

const STATE_CONFIG: Record<TestItemState, { icon: string; color: string; animate?: boolean }> = {
  pending:  { icon: '○', color: '#555' },
  running:  { icon: '◉', color: '#569cd6', animate: true },
  passed:   { icon: '✓', color: '#4ec9b0' },
  failed:   { icon: '✗', color: '#f44747' },
  error:    { icon: '⚠', color: '#ce9178' },
  skipped:  { icon: '⊘', color: '#666' },
};

function stateIcon(state: TestItemState) {
  const cfg = STATE_CONFIG[state];
  return (
    <span style={{
      color: cfg.color,
      marginRight: 6,
      fontSize: 11,
      display: 'inline-block',
      ...(cfg.animate ? { animation: 'pulse 1s ease-in-out infinite' } : {}),
    }}>
      {cfg.icon}
    </span>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────

/**
 * Streaming test results panel — shows live per-test progress during execution
 * and final results when complete. Suites are collapsible.
 */
export function TestResultsPanel() {
  const { testResults, testProgress, isRunningTests } = useSandboxRunner();

  // While running, show streaming progress
  if (isRunningTests || testProgress.phase === 'running') {
    return <StreamingPanel progress={testProgress} />;
  }

  // After completion, show final results (with streaming state as fallback)
  if (testProgress.phase === 'complete') {
    return <FinalPanel results={testResults} progress={testProgress} />;
  }

  // Idle with previous results
  if (testResults && testResults.totalTests > 0) {
    return <FinalPanel results={testResults} progress={testProgress} />;
  }

  return null;
}

// ─── Streaming (in-progress) view ────────────────────────────────────────────

function StreamingPanel({ progress }: { progress: TestProgressState }) {
  const running = progress.suites.flatMap(s => s.tests).filter(t => t.state === 'running').length;
  const done = progress.suites.flatMap(s => s.tests).filter(t => t.state === 'passed' || t.state === 'failed' || t.state === 'error').length;

  return (
    <div style={panelStyle} data-testid="test-results-panel" data-status="running">
      <div style={headerStyle}>
        <span style={{ ...spinnerIconStyle, animation: 'spin 1s linear infinite' }}>⟳</span>
        <span style={{ color: '#569cd6' }}>Running tests...</span>
        {done > 0 && (
          <span style={{ color: '#666', marginLeft: 8, fontSize: 10 }}>
            {done} done{running > 0 ? `, ${running} active` : ''}
          </span>
        )}
      </div>
      <div style={treeContainerStyle}>
        {progress.suites.map((suite, i) => (
          <StreamingSuiteNode key={`${suite.name}-${i}`} suite={suite} />
        ))}
      </div>
      <style>{keyframes}</style>
    </div>
  );
}

function StreamingSuiteNode({ suite }: { suite: SuiteProgressItem }) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useCallback(() => setCollapsed(c => !c), []);

  return (
    <div style={{ marginBottom: 2 }}>
      <div style={suiteHeaderStyle} onClick={toggle} role="button" tabIndex={0}>
        <span style={chevronStyle}>{collapsed ? '▸' : '▾'}</span>
        {stateIcon(suite.state)}
        <span style={{ color: '#ccc', flex: 1 }}>{suite.name}</span>
      </div>
      {!collapsed && suite.tests.map((test, i) => (
        <StreamingTestNode key={`${test.name}-${i}`} test={test} />
      ))}
    </div>
  );
}

function StreamingTestNode({ test }: { test: TestProgressItem }) {
  const cfg = STATE_CONFIG[test.state];
  return (
    <div style={testRowStyle}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {stateIcon(test.state)}
        <span style={{ color: cfg.color === '#555' ? '#777' : test.state === 'passed' ? '#aaa' : cfg.color, fontSize: 12 }}>
          {test.name || '…'}
        </span>
        {test.duration != null && test.state !== 'pending' && test.state !== 'running' && (
          <span style={{ color: '#555', marginLeft: 8, fontSize: 10 }}>
            {test.duration.toFixed(0)}ms
          </span>
        )}
      </div>
      {/* `TestProgressItem.error` is a plain string (SandboxRunnerProvider
          flattens the engine's `TestErrorInfo` to `.message` when it handles
          `test-end`), not the object this used to reach into. */}
      {test.error && (
        <div style={errorStyle}>{test.error}</div>
      )}
    </div>
  );
}

// ─── Final (complete) view ───────────────────────────────────────────────────

function FinalPanel({ results, progress }: { results: TestRunResult | null; progress: TestProgressState }) {
  // Prefer streaming progress data if available (has richer state), fall back to results
  if (progress.phase === 'complete' && progress.suites.length > 0) {
    return <FinalFromProgress progress={progress} results={results} />;
  }

  if (!results || results.totalTests === 0) return null;

  return (
    <div style={panelStyle} data-testid="test-results-panel" data-status="complete"
      data-passed={results.totalPassed} data-failed={results.totalFailed} data-total={results.totalTests}>
      <SummaryHeader passed={results.totalPassed} failed={results.totalFailed}
        total={results.totalTests} duration={results.duration} results={results} />
      <div style={treeContainerStyle}>
        {results.suites.map((suite, i) => (
          <FinalSuiteFromResult key={`${suite.name}-${i}`} suite={suite} />
        ))}
      </div>
      <style>{keyframes}</style>
    </div>
  );
}

function FinalFromProgress({ progress, results }: { progress: TestProgressState; results: TestRunResult | null }) {
  const allTests = progress.suites.flatMap(s => s.tests);
  const passed = allTests.filter(t => t.state === 'passed').length;
  const failed = allTests.filter(t => t.state === 'failed' || t.state === 'error').length;
  const total = allTests.length;

  return (
    <div style={panelStyle} data-testid="test-results-panel" data-status="complete"
      data-passed={passed} data-failed={failed} data-total={total}>
      <SummaryHeader passed={passed} failed={failed} total={total}
        duration={results?.duration ?? 0} results={results} />
      <div style={treeContainerStyle}>
        {progress.suites.map((suite, i) => (
          <StreamingSuiteNode key={`${suite.name}-${i}`} suite={suite} />
        ))}
      </div>
      <style>{keyframes}</style>
    </div>
  );
}

function SummaryHeader({ passed, failed, total, duration, results }: {
  passed: number; failed: number; total: number; duration: number;
  /** The run's result, for what kind of run it was: a cancelled one, or a rerun of one test. */
  results?: TestRunResult | null;
}) {
  const allPassed = failed === 0;
  const color = allPassed ? '#4ec9b0' : '#f44747';
  const icon = allPassed ? '✓' : '✗';

  return (
    <div style={{ ...headerStyle, borderBottom: `1px solid ${color}33` }}>
      <span style={{ color, fontWeight: 'bold', marginRight: 6 }}>{icon}</span>
      <span style={{ color }}>
        {passed}/{total} passed
      </span>
      {failed > 0 && (
        <span style={{ color: '#f44747', marginLeft: 6 }}>
          ({failed} failed)
        </span>
      )}
      <span style={{ color: '#666', marginLeft: 8, fontSize: 10 }}>
        {duration.toFixed(0)}ms
      </span>
      {results?.cancelled && (
        <span className="test-results-cancelled" style={{ color: '#cca700', marginLeft: 8 }}>cancelled</span>
      )}
      {results?.rerun && (
        <span className="test-results-rerun" style={{ color: '#888', marginLeft: 8 }}>reran “{results.rerun.testName}”</span>
      )}
    </div>
  );
}

function FinalSuiteFromResult({ suite }: { suite: TestRunResult['suites'][number] }) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useCallback(() => setCollapsed(c => !c), []);
  const allPassed = suite.failed === 0;

  return (
    <div style={{ marginBottom: 2 }}>
      <div style={suiteHeaderStyle} onClick={toggle} role="button" tabIndex={0}>
        <span style={chevronStyle}>{collapsed ? '▸' : '▾'}</span>
        {stateIcon(allPassed ? 'passed' : 'failed')}
        <span style={{ color: '#ccc', flex: 1 }}>{suite.name}</span>
        <span style={{ color: '#666', fontSize: 10 }}>
          {suite.passed}/{suite.tests.length}
        </span>
      </div>
      {!collapsed && suite.tests.map((test, i) => {
        const state: TestItemState = test.status === 'passed' ? 'passed'
          : test.status === 'failed' ? 'failed'
          : test.status === 'skipped' ? 'skipped' : 'error';
        return (
          <div key={`${test.name}-${i}`} style={testRowStyle}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {stateIcon(state)}
              <span style={{ color: state === 'passed' ? '#aaa' : STATE_CONFIG[state].color, fontSize: 12 }}>
                {test.name}
              </span>
              {/* Optional: the engine leaves per-test durations out of the
                  compared result (see runner/types.ts), so only the synthetic
                  load-error rows ever carry one. Guarded exactly like the live
                  progress view above. */}
              {test.duration != null && (
                <span style={{ color: '#555', marginLeft: 8, fontSize: 10 }}>
                  {test.duration.toFixed(0)}ms
                </span>
              )}
            </div>
            {test.error?.message && <div style={errorStyle}>{test.error.message}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const keyframes = `
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
`;

const panelStyle: React.CSSProperties = {
  backgroundColor: '#1a1a1a',
  borderTop: '1px solid #333',
  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  fontSize: 12,
  maxHeight: 260,
  overflowY: 'auto',
};

const headerStyle: React.CSSProperties = {
  padding: '4px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  color: '#ccc',
  fontSize: 12,
};

const spinnerIconStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 14,
  marginRight: 4,
};

const treeContainerStyle: React.CSSProperties = {
  padding: '2px 8px 6px',
};

const suiteHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '3px 0',
  fontSize: 12,
  cursor: 'pointer',
  userSelect: 'none',
};

const chevronStyle: React.CSSProperties = {
  color: '#666',
  marginRight: 4,
  fontSize: 10,
  width: 12,
  textAlign: 'center',
};

const testRowStyle: React.CSSProperties = {
  paddingLeft: 28,
  padding: '2px 0 2px 28px',
  display: 'flex',
  flexDirection: 'column',
};

const errorStyle: React.CSSProperties = {
  color: '#f44747',
  fontSize: 10,
  paddingLeft: 18,
  fontStyle: 'italic',
  marginTop: 1,
  wordBreak: 'break-word',
};
